import { NotFound, Page, RouteComponent } from "@metablock/react";
import { LinkIcon, MailIcon } from "@metaweb/Components/Icons";
import OrgSvg from "@metaweb/icons/users-group.svg";
import { useStores } from "@metaweb/stores";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, useParams } from "react-router-dom";
import { useAsync } from "react-use";
import { useMetablockApp } from "../../Extensions";
import ErrorFallback from "../Components/ErrorFallback";
import Header from "../Components/Header";
import List from "../Components/List";
import TabRoutes from "../Components/TabRoutes";
import OrgExtensions from "./Extensions";
import OrgMembers from "./Members";
import OrgRoles from "./Roles";
import OrgSettings from "./Settings";
import SpaceList from "./SpaceList";

const OrgIcon = (props: any) => {
  const { width, ...extra } = props;
  return (
    <OrgSvg width={width} height={width} viewBox="0 0 537 537" {...extra} />
  );
};

const OrgHome = () => {
  const { org_name } = useParams() as { org_name: string };
  const { orgStore } = useStores();

  const app = useMetablockApp({
    baseUrl: `/app/orgs/${org_name}`,
    routes: [
      {
        label: "Settings",
        url: "settings",
        Component: OrgSettings,
      },
      {
        label: "Extensions",
        url: "extensions",
        routePath: "extensions/*",
        Component: OrgExtensions,
      },
      {
        label: "Members",
        url: "members",
        Component: OrgMembers,
      },
      {
        label: "Roles",
        url: "roles",
        routePath: "roles/*",
        Component: OrgRoles,
      },
      {
        label: "Spaces",
        url: "",
        routePath: "*",
        Component: SpaceList,
      },
    ],
  });

  const { loading, value } = useAsync(async () => {
    return await orgStore.getOrg(org_name);
  }, [org_name]);
  if (loading) return null;
  if (!value) return <NotFound />;
  const org = value;

  const Tabs = (opts: any) => {
    const { Component, url, ...props } = opts;
    return (
      <TabRoutes
        routes={app.getNavigation()}
        value={url}
        textColor="primary"
        indicatorColor="primary"
        sx={{ backgroundColor: "palette.background.default" }}
        maxWidth="lg"
        centered
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Component baseUrl={url} {...props} />
        </ErrorBoundary>
      </TabRoutes>
    );
  };

  return (
    <Page title={`${org.short_name} org`}>
      <Box bgcolor="action.hover">
        <Container maxWidth="md">
          <Box p={3}>
            <Header Icon={OrgIcon}>
              <Typography variant="h4" component="h1" paragraph>
                {org.short_name}
              </Typography>
              <Typography variant="subtitle2" component="h3">
                {org.full_name}
              </Typography>
              <Typography variant="subtitle2" component="div">
                <List
                  direction="horizontal"
                  items={[
                    { href: org.website, icon: <LinkIcon /> },
                    { text: org.email, icon: <MailIcon /> },
                  ]}
                />
              </Typography>
            </Header>
          </Box>
        </Container>
        <Routes>
          {app.routes.map(({ url, routePath, Component }: RouteComponent) => (
            <Route
              path={routePath || url}
              key={url}
              element={
                <Tabs Component={Component} org={org} url={app.getUrl(url)} />
              }
            />
          ))}
        </Routes>
      </Box>
    </Page>
  );
};

export default OrgHome;
